import {
  createAbonnementTemplate,
  listPuckPages,
} from "@/api/cms-puck/api";
import {
  useGeneralSettingsFormData,
  useUpdateGeneralSettings,
} from "@/api/cms";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/auth/AuthContext";
import { CmsAdminShell } from "@/pages/cms/advanced/AdminShell";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase, puckPageStatusLabel } from "@/lib/cms/cms-new-phrases";

const PAGES_KEY = ["cms-puck", "pages"] as const;
const SETTINGS_KEY = ["cms", "general-settings"] as const;
const NONE_VALUE = "__none__";

export default function CMSAdvancedAbonnement() {
  const cmsNew = useCmsNew();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { instanceData } = useAuth();

  const { data: generalSettings, isLoading: settingsLoading } =
    useGeneralSettingsFormData();
  const pagesQuery = useQuery({
    // Clé distincte de la liste Pages (complète) pour ne pas partager le cache.
    queryKey: [
      ...PAGES_KEY,
      {
        excludeCatalogFlow: true,
        excludeMemberFlow: true,
        excludeShellFlow: true,
        excludeSearchFlow: true,
        excludeMaintenancePage: true,
      },
    ],
    queryFn: () =>
      listPuckPages({
        excludeCatalogFlow: true,
        excludeMemberFlow: true,
        excludeShellFlow: true,
        excludeSearchFlow: true,
        excludeMaintenancePage: true,
      }),
  });

  const updateSettings = useUpdateGeneralSettings();

  const createTemplateMutation = useMutation({
    mutationFn: createAbonnementTemplate,
    onSuccess: ({ page }) => {
      void queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      void queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
      toast({
        title: cmsNew("cms.new.auto.title.page_dabonnement_creee", "Page d'abonnement créée"),
        variant: "success",
        duration: 1600,
      });
      navigate(`/cms/advanced/${page.id}`);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? error.message
          : cmsPhrase(cmsNew, "createAbonnementPageFailed");
      toast({ title: message, variant: "error" });
    },
  });

  const abonnementPageId = generalSettings?.abonnementPageId ?? null;
  const cmsId = generalSettings?.id ?? instanceData?.CMS?.id;

  const assigned = pagesQuery.data?.find((p) => p.id === abonnementPageId);
  const isDraft = assigned && assigned.status !== "PUBLISHED";

  const saveSettings = (patch: { abonnementPageId?: string | null }) => {
    if (!cmsId) {
      toast({
        title: cmsNew("cms.new.auto.title.impossible_denregistrer_cms_introuvable", "Impossible d'enregistrer : CMS introuvable"),
        variant: "error",
      });
      return;
    }
    updateSettings.mutate(
      {
        id: cmsId,
        headerAlwaysVisible: generalSettings?.headerAlwaysVisible ?? false,
        headerAlwaysVisibleMobile:
          generalSettings?.headerAlwaysVisibleMobile ?? false,
        socials: generalSettings?.socials ?? [],
        isMixedCRMode: generalSettings?.isMixedCRMode ?? false,
        defaultWebsiteLanguage:
          generalSettings?.defaultWebsiteLanguage ?? "fr",
        websiteLanguages: generalSettings?.websiteLanguages ?? ["fr"],
        playerAutoplay: generalSettings?.playerAutoplay ?? false,
        playerControls: generalSettings?.playerControls ?? true,
        playerMuted: generalSettings?.playerMuted ?? false,
        playerLoop: generalSettings?.playerLoop ?? false,
        maintenanceEnabled: generalSettings?.maintenanceEnabled ?? false,
        maintenancePageId: generalSettings?.maintenancePageId ?? null,
        abonnementPageId,
        ...patch,
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
          toast({
            title: cmsNew("cms.new.auto.title.page_dabonnement_enregistree", "Page d'abonnement enregistrée"),
            variant: "success",
            duration: 1600,
          });
        },
        onError: () => {
          toast({
            title: cmsNew("cms.new.auto.title.impossible_denregistrer", "Impossible d'enregistrer"),
            variant: "error",
          });
        },
      },
    );
  };

  return (
    <CmsAdminShell
      title={cmsNew("cms.new.auto.title.abonnement", "Abonnement")}
      subtitle={cmsPhrase(cmsNew, "abonnementSubtitle")}
    >
      <div className="space-y-6 px-6 py-4">
        <p className="text-sm text-slate-600">
          {cmsNew(
            "cms.new.auto.description.abonnement_page_puck",
            "Lorsqu'une page CMS avancé publiée est assignée, elle remplace la page d'abonnement classique sur /abonnement. Utilisez des blocs Bouton paiement en mode Consultation (abonnement) pour déclencher le checkout Stripe.",
          )}
        </p>

        {settingsLoading ? (
          <p className="text-sm text-slate-500">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>
        ) : (
          <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <Label className="text-sm font-medium text-slate-800">
                {cmsPhrase(cmsNew, "abonnementPageLabel")}
              </Label>
              <code className="text-[11px] text-slate-400">/abonnement</code>
            </div>
            <Select
              value={abonnementPageId ?? NONE_VALUE}
              onValueChange={(next) =>
                saveSettings({
                  abonnementPageId: next === NONE_VALUE ? null : next,
                })
              }
              disabled={updateSettings.isPending || pagesQuery.isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder={cmsNew("cms.new.auto.placeholder.aucune_page_classique", "Aucune (page classique)")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>{cmsNew("cms.new.auto.selectItem.aucune_page_classique", "Aucune (page classique)")}</SelectItem>
                {(pagesQuery.data ?? []).map((page) => (
                  <SelectItem key={page.id} value={page.id}>
                    {page.name}
                    {page.status !== "PUBLISHED" ? ` (${puckPageStatusLabel(cmsNew, page.status)})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {isDraft ? (
              <p className="text-xs text-amber-700">
                {cmsPhrase(cmsNew, "draftPublishHint")}
              </p>
            ) : null}

            <div className="flex flex-wrap gap-2 pt-1">
              {abonnementPageId ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1"
                  onClick={() => navigate(`/cms/advanced/${abonnementPageId}`)}
                >
                  <ExternalLink size={14} />
                  {cmsPhrase(cmsNew, "editTemplate")}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 gap-1"
                  disabled={createTemplateMutation.isPending}
                  onClick={() => createTemplateMutation.mutate()}
                >
                  <Plus size={14} />
                  {createTemplateMutation.isPending
                    ? cmsPhrase(cmsNew, "creating")
                    : cmsPhrase(cmsNew, "createTemplate")}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </CmsAdminShell>
  );
}
